
import axios from 'axios';
import { adalConfig, authContext } from '../../Utils/AdalConfig';
import { HandleAPIError, HandleExceededTimeAPIError, HandleUIError } from '../../Utils/EventGA';
import moment from 'moment';


const base = "";

// --- Session recovery ------------------------------------------------------
// When silent renewal (acquireToken) fails, the Azure AD session is gone
// (login_required). Instead of failing the request we PARK it — its promise
// stays pending in a queue — and signal the app-level SessionExpiredModal.
// After the user re-authenticates in another tab the queue is replayed with
// the fresh token, so the original callbacks still receive their data.

const SESSION_EXPIRED_ERROR = 'SESSION_EXPIRED';
const AUTH_TIMEOUT_ERROR = 'Auth timeout - silent token refresh failed';

export const SESSION_EXPIRED_EVENT = 'voucher:sessionExpired';

const MAX_PENDING_REQUESTS = 100;
const MAX_RECOVERY_ATTEMPTS = 2;
// adal may never invoke the acquireToken callback (hung renewal iframe) — without the reject the promise stays open forever
const ACQUIRE_TOKEN_TIMEOUT_MS = 15000;

let pendingRequests = [];
let expiredUserName = null;

// Marks an error as "session expired" so the catch handlers can suppress the
// generic error for it.
const makeSessionExpiredError = () =>
    Object.assign(new Error(SESSION_EXPIRED_ERROR), { isSessionExpired: true });

// Callers must dispatch notifySessionExpired BEFORE parking — the queue-full
// rejection below relies on that invariant for the modal to be up already.
const parkRequest = (send, reject) => {
    if (pendingRequests.length >= MAX_PENDING_REQUESTS) {
        reject(makeSessionExpiredError());
        return;
    }
    pendingRequests.push({ send, reject });
};

// The event is dispatched ALWAYS (the modal dedupes while open and needs it to
// reopen); only telemetry is limited to the first recovery attempt per request.
const notifySessionExpired = (url, logTelemetry = true) => {
    if (logTelemetry) {
        HandleUIError(AUTH_TIMEOUT_ERROR, url);
    }
    if (!expiredUserName) {
        expiredUserName = authContext.getCachedUser()?.userName || null;
    }
    window.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT));
};

export const getPendingSessionRequestCount = () => pendingRequests.length;

export const retrySessionRenewal = () => new Promise((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error('acquireToken timeout')), ACQUIRE_TOKEN_TIMEOUT_MS);
    // the callback is deliberately NOT gated on the timeout having fired: a token that lands late still
    // replays the parked queue (the user's work completes); the modal self-heals on its next attempt
    authContext.acquireToken(adalConfig.endpoints.api, (_, token) => {
        clearTimeout(timer);
        if (!token) {
            resolve(false);
            return;
        }
        const renewedUserName = authContext.getCachedUser()?.userName || null;
        if (expiredUserName && renewedUserName && expiredUserName !== renewedUserName) {
            // renewed with a DIFFERENT account: replaying the parked requests would run them as another user
            window.location.reload();
            // settle the promise so nothing stays pending while the reload tears the page down
            resolve(false);
            return;
        }
        expiredUserName = null;
        const parked = pendingRequests;
        pendingRequests = [];
        // a sync throw from one send must not strand the rest of the queue with promises pending forever
        parked.forEach(p => {
            try { p.send(token); } catch (e) { p.reject(e); }
        });
        resolve(true);
    });
});

// Centralized catch handler: swallow the generic error for session-expired
// cases (the modal already informed the user), otherwise behave as before.
const handleCatchError = (err, callback, url) => {
    if (err?.isSessionExpired) {
        callback({ isLoading: false });
        return;
    }
    HandleAPIError(err?.message, url);
    callback({ error: err, isLoading: false });
};

// Add a request interceptor
const instance = axios.create(adalConfig)
instance.interceptors.request.use((config) => {
    // Check and acquire a token before the request is sent
    return new Promise((resolve, reject) => {
        authContext.acquireToken(adalConfig.endpoints.api, (message, token, msg) => {
            if (!!token) {
                config.headers.Authorization = `bearer ${token}`;
                resolve(config)
            } else if (config.skipSessionRecovery) {
                reject(makeSessionExpiredError())
            } else {
                notifySessionExpired(config.url);
                parkRequest(newToken => {
                    config.headers.Authorization = `bearer ${newToken}`;
                    resolve(config);
                }, reject);
            }
        })
    })
}, function (error) {

    return Promise.reject(error)
})

// Add a response interceptor
// On a 401 (token invalid/expired, e.g. after inactivity or a deploy), silently
// renew the token via acquireToken and replay the original request exactly once;
// if renewal needs user interaction the request is parked until the
// SessionExpiredModal flow produces a fresh token.
instance.interceptors.response.use(
    response => response,
    (error) => {
        const originalRequest = error.config;
        if (error.response?.status !== 401 || !originalRequest) {
            return Promise.reject(error);
        }
        if (!originalRequest._retry) {
            originalRequest._retry = true;

            return new Promise((resolve, reject) => {
                authContext.acquireToken(adalConfig.endpoints.api, (message, token) => {
                    if (!!token) {
                        originalRequest.headers.Authorization = `bearer ${token}`;
                        resolve(instance(originalRequest));
                    } else if (originalRequest.skipSessionRecovery) {
                        reject(makeSessionExpiredError());
                    } else {
                        notifySessionExpired(originalRequest.url);
                        parkRequest(newToken => {
                            originalRequest.headers.Authorization = `bearer ${newToken}`;
                            resolve(instance(originalRequest));
                        }, reject);
                    }
                });
            });
        }
        // 401 with a freshly renewed token: the cache holds a token the backend no
        // longer accepts (e.g. revoked session); without clearing it acquireToken
        // would keep serving it and the modal would close without fixing anything.
        const attempts = (originalRequest._sessionRecoveryAttempts || 0) + 1;
        if (originalRequest.skipSessionRecovery || attempts > MAX_RECOVERY_ATTEMPTS) {
            return Promise.reject(makeSessionExpiredError());
        }
        originalRequest._sessionRecoveryAttempts = attempts;
        authContext.clearCacheForResource(adalConfig.endpoints.api);
        return new Promise((resolve, reject) => {
            notifySessionExpired(originalRequest.url, attempts === 1);
            parkRequest(newToken => {
                originalRequest.headers.Authorization = `bearer ${newToken}`;
                resolve(instance(originalRequest));
            }, reject);
        });
    }
)

export function getAPI(callback, url) {
    const timerStart = moment();

    instance.get(base+url)
        .then(res => {
            callback({ data: res.data, isLoading: false });

            const timerEnd = moment();
            const diff = timerEnd.diff(timerStart, 'seconds');
            if (diff > 20)
                HandleExceededTimeAPIError(diff, url);
        })
        .catch(err => handleCatchError(err, callback, url));
}


export function postAPI(callback, url, body) {
    const timerStart = moment();

    instance.post(base +url, body, { withCredentials: true, headers: { 'Content-Type': 'application/json' } })
        .then(res => {
            callback({ data: res.data, isLoading: false });

            const timerEnd = moment();
            const diff = timerEnd.diff(timerStart, 'seconds');
            if (diff > 20)
                HandleExceededTimeAPIError(diff, url);
        })
        .catch(err => handleCatchError(err, callback, url));
}

export function putAPI(callback, url, body) {
    const timerStart = moment();

    instance.put(base +url, body, { withCredentials: true, headers: { 'Content-Type': 'application/json' } })
        .then(res => {
            callback({ data: res.data, isLoading: false });

            const timerEnd = moment();
            const diff = timerEnd.diff(timerStart, 'seconds');
            if (diff > 20)
                HandleExceededTimeAPIError(diff, url);
        })
        .catch(err => handleCatchError(err, callback, url));
}


export function patchAPI(callback, url, body) {
    const timerStart = moment();

    instance.patch(base +url, body, { withCredentials: true })
        .then(res => {
            callback({ data: res.data, isLoading: false });

            const timerEnd = moment();
            const diff = timerEnd.diff(timerStart, 'seconds');
            if (diff > 20)
                HandleExceededTimeAPIError(diff, url);
        })
        .catch(err => handleCatchError(err, callback, url));
}

export function postMultiPartAPI(callback, url, body) {
    const timerStart = moment();

    instance.post(base +url, body, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } })
        .then(res => {
            callback({ data: res.data, isLoading: false });

            const timerEnd = moment();
            const diff = timerEnd.diff(timerStart, 'seconds');
            if (diff > 20)
                HandleExceededTimeAPIError(diff, url);
        })
        .catch(err => handleCatchError(err, callback, url));
}

export function deleteAPI(callback, url, body) {
    const timerStart = moment();

    instance.delete(base +url, body, { withCredentials: true, headers: { 'Content-Type': 'application/json' } })
        .then(res => {
            callback({ data: res.data, isLoading: false });

            const timerEnd = moment();
            const diff = timerEnd.diff(timerStart, 'seconds');
            if (diff > 20)
                HandleExceededTimeAPIError(diff, url);
        })
        .catch(err => handleCatchError(err, callback, url));
}
